import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { statsService, userService, apprenantService, actionService, reportingService, atelierService } from '../services/api';
import { Users, LogOut, FileDown, Download, RefreshCw, Settings, Calendar, Bell, FileText, Briefcase, BarChart3, Shield, Key, ThumbsUp, DoorOpen, FolderDown, MapPin, ChevronDown, Building2, GraduationCap, MessageCircle, FileSignature, ClipboardCheck, Clock, Presentation, UserCheck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import ResponsiveTabs from '../components/ResponsiveTabs';
import { useNavigate } from 'react-router-dom';
import { useContratAutoRefresh } from '../hooks/useContratAutoRefresh';
import LiveIndicator from '../components/LiveIndicator';

// Composants extraits (refactoring)
import { AdminUsersView, AdminBeneficiairesView, AdminAteliersView, AdminDownloadsView, AdminConsentementsView, AdminJournalAccesView } from './admin';
import AdminNouveauxEntrantsView from './admin/AdminNouveauxEntrantsView';

// Composants existants
import NotificationsPanel from '../components/NotificationsPanel';
import CalendarView from '../components/CalendarView';
import PermissionsManager from '../components/PermissionsManager';
import SyntheseGenerator from '../components/SyntheseGenerator';
import BourseEmploi from '../components/BourseEmploi';
import CredentialsManager from '../components/CredentialsManager';
import SatisfactionManager from '../components/SatisfactionManager';
import SortiesManager from '../components/SortiesManager';
import DocumentsPartages from '../components/DocumentsPartages';
import ProjetsManager from '../components/ProjetsManager';
import SyntheseCollective from '../components/SyntheseCollective';
import ContratsManager from '../components/ContratsManager';
import ExportManager from '../components/ExportManager';
import PeriodesEssaiManager from '../components/PeriodesEssaiManager';
import EntreprisesManager from '../components/EntreprisesManager';
import OffresEnAttenteAdmin from '../components/OffresEnAttenteAdmin';
import Messagerie from '../components/Messagerie';
import MessagerieBadge from '../components/MessagerieBadge';
import EmailComposeModal from '../components/email/EmailComposeModal';
import SynthesesManquantes from '../components/SynthesesManquantes';
import EditActionDialog from '../components/EditActionDialog';
import ValidationDiagnostics from '../components/ValidationDiagnostics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // État global
  const [stats, setStats] = useState(null);
  const [zone, setZone] = useState('all');
  const [availableZones] = useState(['all', 'Saint-Pierre', 'Sainte-Marie']);
  const [users, setUsers] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState(null);
  const [actions, setActions] = useState([]);
  const [beneficiaireStats, setBeneficiaireStats] = useState(null);
  const [beneficiaireAteliers, setBeneficiaireAteliers] = useState([]);
  const [ateliers, setAteliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('synthese');

  // États d'édition
  const [editingUser, setEditingUser] = useState(null);
  const [editingAction, setEditingAction] = useState(null);
  const [editingAtelier, setEditingAtelier] = useState(null);

  // Modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalDefaults, setEmailModalDefaults] = useState({});
  const [showSyntheseGenerator, setShowSyntheseGenerator] = useState(false);
  const [showSyntheseBenefDialog, setShowSyntheseBenefDialog] = useState(false);
  const [synthBenefForm, setSynthBenefForm] = useState({
    beneficiaire_ids: [],
    mois_debut: new Date().getMonth() + 1,
    annee_debut: new Date().getFullYear(),
    mois_fin: new Date().getMonth() + 1,
    annee_fin: new Date().getFullYear(),
    selectAll: false
  });
  const [generatingSynth, setGeneratingSynth] = useState(false);

  // Auto-refresh contrats — recharge le dashboard dès qu'une signature est détectée
  const handleContratUpdate = useCallback(() => {
    loadData();
  }, []);

  const { isConnected, isPolling, lastPollTime } = useContratAutoRefresh(
    handleContratUpdate, 
    30000, 
    view === 'synthese' || view === 'contrats'
  );

  // Chargement initial
  useEffect(() => {
    loadData();
  }, []);

  // Chargement des ateliers quand on switch sur la vue ateliers
  useEffect(() => {
    if (view === 'ateliers') {
      loadAteliers();
    }
  }, [view]);

  // Chargement données bénéficiaire sélectionné
  useEffect(() => {
    if (selectedBeneficiaire) {
      loadBeneficiaireData(selectedBeneficiaire.id);
    }
  }, [selectedBeneficiaire]);

  const loadAteliers = async () => {
    try {
      const response = await atelierService.getAll();
      setAteliers(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des ateliers');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, beneficiairesRes] = await Promise.all([
        statsService.getGlobal(),
        userService.getAll(),
        apprenantService.getAll()
      ]);

      setStats(statsRes.data);
      setZone(statsRes.data.zone);
      setUsers(usersRes.data);
      setBeneficiaires(beneficiairesRes.data);

    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadBeneficiaireData = async (beneficiaireId) => {
    try {
      const [actionsRes, statsRes, ateliersRes] = await Promise.all([
        actionService.getAll(beneficiaireId),
        actionService.getStats(beneficiaireId),
        atelierService.getAll()
      ]);
      setActions(actionsRes.data);
      setBeneficiaireStats(statsRes.data);
      
      // Filtrer les ateliers où le bénéficiaire est participant
      // On vérifie p.id ET p.apprenant_id pour couvrir les anciens et nouveaux documents
      const ateliersDuBeneficiaire = ateliersRes.data.filter(atelier =>
        atelier.participants?.some(p => p.id === beneficiaireId || p.apprenant_id === beneficiaireId)
      );
      setBeneficiaireAteliers(ateliersDuBeneficiaire);
    } catch (error) {
      toast.error('Erreur de chargement des données du bénéficiaire');
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedBeneficiaire) return;
    const now = new Date();
    const mois = now.getMonth() + 1;
    const annee = now.getFullYear();

    try {
      const response = await reportingService.getSyntheseIndividuelle(selectedBeneficiaire.id, mois, annee);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `synthese_${selectedBeneficiaire.nom}_${mois}_${annee}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur de génération du PDF');
    }
  };

  const handleGenerateSyntheseBenef = async () => {
    const { beneficiaire_ids, mois_debut, annee_debut, mois_fin, annee_fin } = synthBenefForm;
    if (beneficiaire_ids.length === 0) {
      toast.error('Veuillez sélectionner au moins un bénéficiaire');
      return;
    }
    setGeneratingSynth(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reporting/synthese-groupee`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ beneficiaire_ids, mois_debut: parseInt(mois_debut), annee_debut: parseInt(annee_debut), mois_fin: parseInt(mois_fin), annee_fin: parseInt(annee_fin) })
      });
      if (!response.ok) throw new Error('Erreur de génération');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const nbBenef = beneficiaire_ids.length;
      const filename = nbBenef === 1 
        ? `synthese_individuelle_${mois_debut}_${annee_debut}.pdf`
        : `synthese_groupee_${nbBenef}_beneficiaires_${mois_debut}_${annee_debut}.pdf`;
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      toast.success(`Synthèse ${nbBenef > 1 ? 'groupée' : 'individuelle'} générée`);
      setShowSyntheseBenefDialog(false);
      setSynthBenefForm({ beneficiaire_ids: [], mois_debut: new Date().getMonth() + 1, annee_debut: new Date().getFullYear(), mois_fin: new Date().getMonth() + 1, annee_fin: new Date().getFullYear(), selectAll: false });
    } catch (error) {
      toast.error('Erreur lors de la génération de la synthèse');
    } finally {
      setGeneratingSynth(false);
    }
  };

  // Onglets principaux (visibles en permanence dans la barre)
  const mainTabs = [
    { value: 'synthese', label: 'Synthèse Collective', icon: BarChart3 },
    { value: 'beneficiaires', label: 'Bénéficiaires', icon: GraduationCap },
    { value: 'messagerie', label: 'Messagerie', icon: MessageCircle },
    { value: 'calendar', label: 'Calendrier', icon: Calendar },
    { value: 'bourse', label: 'Bourse à l\'Emploi', icon: Briefcase },
    { value: 'documents', label: 'Documents', icon: FileText },
  ];

  // Options secondaires dans le menu déroulant "Plus"
  const secondaryOptions = [
    // --- Gestion du dispositif ---
    { value: 'users', label: 'Utilisateurs', icon: Users, group: 'Gestion' },
    { value: 'ateliers', label: 'Ateliers', icon: Presentation, group: 'Gestion' },
    { value: 'contrats', label: 'Contrats', icon: FileSignature, group: 'Gestion' },
    // --- Suivi & validation ---
    { value: 'nouveaux-entrants', label: 'Nouveaux entrants', icon: UserCheck, group: 'Suivi' },
    { value: 'consentements', label: 'Consentements', icon: ClipboardCheck, group: 'Suivi' },
    { value: 'offres-attente', label: 'Offres à Valider', icon: ClipboardCheck, group: 'Suivi' },
    { value: 'periodes-essai', label: 'Périodes d\'Essai', icon: Clock, group: 'Suivi' },
    { value: 'satisfaction', label: 'Satisfaction', icon: ThumbsUp, group: 'Suivi' },
    { value: 'diagnostics', label: 'Diagnostics de compétences', icon: ClipboardCheck, group: 'Suivi' },
    { value: 'syntheses-rdv', label: 'Synthèses RDV à compléter', icon: FileText, group: 'Suivi' },
    { value: 'sorties', label: 'Sorties', icon: DoorOpen, group: 'Suivi' },
    // --- Configuration & données ---
    { value: 'entreprises', label: 'Base de données Entreprises', icon: Building2, group: 'Configuration' },
    { value: 'notifications', label: 'Notifications', icon: Bell, group: 'Configuration' },
    { value: 'projets', label: 'Projets', icon: BarChart3, group: 'Configuration' },
    { value: 'credentials', label: 'Credentials', icon: Key, group: 'Configuration' },
    { value: 'exports', label: 'Exports Données', icon: FolderDown, group: 'Configuration' },
    { value: 'permissions', label: 'Permissions', icon: Shield, group: 'Configuration' },
    { value: 'journal-acces', label: 'Journal des accès', icon: Shield, group: 'Configuration' },
    { value: 'telechargements', label: 'Téléchargements PDF', icon: Download, group: 'Configuration' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Tableau de bord Admin
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">Connecté : {user?.prenom} {user?.nom}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <div className="hidden sm:block">
              <LiveIndicator isConnected={isConnected} isPolling={isPolling} lastPollTime={lastPollTime} />
            </div>

            {/* Sélecteur de zone géographique */}
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="w-32 sm:w-44 h-9 rounded-xl" data-testid="zone-select">
                <MapPin className="w-4 h-4 mr-1 sm:mr-2 text-slate-500 shrink-0" />
                <SelectValue placeholder="Zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes zones</SelectItem>
                <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
              </SelectContent>
            </Select>

            {/* Menu déroulant des options secondaires */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-xl h-9 px-2 sm:px-3" data-testid="options-menu-trigger">
                  <Settings className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Plus</span>
                  <ChevronDown className="w-4 h-4 ml-1 hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 max-h-[70vh] overflow-y-auto">
                {(() => {
                  // Regrouper les options par leur propriété `group`
                  const groups = secondaryOptions.reduce((acc, opt) => {
                    const g = opt.group || 'Autres';
                    if (!acc[g]) acc[g] = [];
                    acc[g].push(opt);
                    return acc;
                  }, {});
                  return Object.entries(groups).map(([groupName, items], groupIdx) => (
                    <React.Fragment key={groupName}>
                      {groupIdx > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel>{groupName}</DropdownMenuLabel>
                      {items.map((option) => {
                        const Icon = option.icon;
                        return (
                          <DropdownMenuItem
                            key={option.value}
                            onClick={() => setView(option.value)}
                            className={view === option.value ? 'bg-blue-50 text-blue-700' : ''}
                            data-testid={`menu-${option.value}`}
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            {option.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </React.Fragment>
                  ));
                })()}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cloche / badge messagerie : compteur de messages non lus */}
            <MessagerieBadge onClick={() => setView('messagerie')} />

            <Button onClick={logout} variant="outline" className="rounded-xl h-9 px-2 sm:px-3" data-testid="logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation par onglets (responsive) */}
      <nav className="bg-white border-b border-slate-200 px-4 sm:px-6 py-2">
        <div className="max-w-7xl mx-auto">
          <ResponsiveTabs
            tabs={mainTabs}
            value={mainTabs.some(t => t.value === view) ? view : ''}
            onValueChange={setView}
            mobilePlaceholder="Choisir une section"
          />

          {/* Indicateur de vue secondaire active */}
          {!mainTabs.some(t => t.value === view) && (
            <div className="mt-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                Vue active : {secondaryOptions.find(o => o.value === view)?.label || view}
              </Badge>
            </div>
          )}
        </div>
      </nav>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {view === 'users' && (
          <AdminUsersView 
            users={users}
            currentUserId={user?.id}
            editingUser={editingUser}
            setEditingUser={setEditingUser}
            onRefresh={loadData}
          />
        )}

        {view === 'beneficiaires' && (
          <AdminBeneficiairesView 
            beneficiaires={beneficiaires}
            selectedBeneficiaire={selectedBeneficiaire}
            setSelectedBeneficiaire={setSelectedBeneficiaire}
            users={users}
            actions={actions}
            beneficiaireStats={beneficiaireStats}
            beneficiaireAteliers={beneficiaireAteliers}
            editingAction={editingAction}
            setEditingAction={setEditingAction}
            onRefresh={loadData}
            onLoadBeneficiaireData={loadBeneficiaireData}
            onGeneratePDF={handleGeneratePDF}
            onShowSyntheseBenefDialog={() => setShowSyntheseBenefDialog(true)}
          />
        )}

        {view === 'ateliers' && (
          <AdminAteliersView 
            ateliers={ateliers}
            users={users}
            editingAtelier={editingAtelier}
            setEditingAtelier={setEditingAtelier}
            onRefresh={loadAteliers}
          />
        )}

        {view === 'synthese' && <SyntheseCollective zone={zone === 'all' ? null : zone} />}
        {view === 'calendar' && <CalendarView userRole="admin" />}
        {view === 'bourse' && <BourseEmploi canCreate canEdit canDelete isBeneficiaire={false} />}
        {view === 'offres-attente' && <OffresEnAttenteAdmin />}
        {view === 'messagerie' && (
          <div>
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => { setEmailModalDefaults({}); setShowEmailModal(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Composer un email
              </Button>
            </div>
            <Messagerie />
          </div>
        )}
        {view === 'notifications' && <NotificationsPanel />}
        {view === 'permissions' && <PermissionsManager />}
        {view === 'journal-acces' && <AdminJournalAccesView />}
        {view === 'credentials' && <CredentialsManager />}
        {view === 'satisfaction' && <SatisfactionManager />}
        {view === 'nouveaux-entrants' && <AdminNouveauxEntrantsView />}

        {view === 'consentements' && <AdminConsentementsView />}
        {view === 'sorties' && <SortiesManager />}
        {view === 'documents' && <DocumentsPartages userRole="admin" />}
        {view === 'projets' && <ProjetsManager />}
        {view === 'contrats' && <ContratsManager userRole="admin" zone={zone === 'all' ? null : zone} />}
        {view === 'periodes-essai' && <PeriodesEssaiManager />}
        {view === 'syntheses-rdv' && (
          <div className="space-y-4">
            <SynthesesManquantes
              onSelectAction={(a) => setEditingAction(a)}
            />
            <EditActionDialog
              action={editingAction}
              open={!!editingAction}
              onOpenChange={(open) => !open && setEditingAction(null)}
              onSuccess={() => {
                // Forcer le re-render du composant SynthesesManquantes
                setEditingAction(null);
                setTimeout(() => setView('syntheses-rdv'), 50);
              }}
            />
          </div>
        )}
        {view === 'diagnostics' && <ValidationDiagnostics />}
        {view === 'exports' && <ExportManager />}
        {view === 'entreprises' && <EntreprisesManager />}
        {view === 'telechargements' && (
          <AdminDownloadsView
            beneficiaires={beneficiaires}
            ateliers={ateliers}
            users={users}
          />
        )}
      </main>

      {/* Modals */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultTo={emailModalDefaults.to || ''}
        defaultSubject={emailModalDefaults.subject || ''}
        defaultBody={emailModalDefaults.body || ''}
      />
      <SyntheseGenerator open={showSyntheseGenerator} onOpenChange={setShowSyntheseGenerator} />

      {/* Dialog Synthèse Bénéficiaires */}
      <Dialog open={showSyntheseBenefDialog} onOpenChange={setShowSyntheseBenefDialog}>
        <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileDown className="w-6 h-6 text-blue-600" />
              Synthèse Bénéficiaires
            </DialogTitle>
            <DialogDescription>
              Générez une synthèse PDF individuelle ou groupée pour une période donnée
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Sélection période */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Période début</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={String(synthBenefForm.mois_debut)} onValueChange={(val) => setSynthBenefForm({...synthBenefForm, mois_debut: parseInt(val)})}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((mois, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>{mois}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={synthBenefForm.annee_debut} onChange={(e) => setSynthBenefForm({...synthBenefForm, annee_debut: parseInt(e.target.value)})} className="w-24 rounded-xl" min={2020} max={2030} />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Période fin</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={String(synthBenefForm.mois_fin)} onValueChange={(val) => setSynthBenefForm({...synthBenefForm, mois_fin: parseInt(val)})}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((mois, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>{mois}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={synthBenefForm.annee_fin} onChange={(e) => setSynthBenefForm({...synthBenefForm, annee_fin: parseInt(e.target.value)})} className="w-24 rounded-xl" min={2020} max={2030} />
                </div>
              </div>
            </div>

            {/* Sélection bénéficiaires */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Sélectionner les bénéficiaires
                  {synthBenefForm.beneficiaire_ids.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700">
                      {synthBenefForm.beneficiaire_ids.length} sélectionné{synthBenefForm.beneficiaire_ids.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </Label>
                <Button variant="ghost" size="sm" onClick={() => {
                  if (synthBenefForm.selectAll) {
                    setSynthBenefForm({...synthBenefForm, beneficiaire_ids: [], selectAll: false});
                  } else {
                    setSynthBenefForm({...synthBenefForm, beneficiaire_ids: beneficiaires.map(b => b.id), selectAll: true});
                  }
                }} className="text-blue-600 hover:text-blue-700">
                  {synthBenefForm.selectAll ? 'Désélectionner tout' : 'Sélectionner tout'}
                </Button>
              </div>
              
              <div className="border rounded-xl max-h-60 overflow-y-auto p-2">
                {beneficiaires.map((ben) => {
                  const isSelected = synthBenefForm.beneficiaire_ids.includes(ben.id);
                  return (
                    <div key={ben.id} onClick={() => {
                      const newIds = isSelected ? synthBenefForm.beneficiaire_ids.filter(id => id !== ben.id) : [...synthBenefForm.beneficiaire_ids, ben.id];
                      setSynthBenefForm({ ...synthBenefForm, beneficiaire_ids: newIds, selectAll: newIds.length === beneficiaires.length });
                    }} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4 text-blue-600 rounded" />
                      <div className="flex-1">
                        <span className="font-medium">{ben.prenom} {ben.nom}</span>
                        <span className="text-xs text-slate-500 ml-2">{ben.zone_geographique}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {synthBenefForm.beneficiaire_ids.length > 0 && (
              <div className={`p-4 rounded-xl ${synthBenefForm.beneficiaire_ids.length === 1 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-sm font-medium ${synthBenefForm.beneficiaire_ids.length === 1 ? 'text-green-700' : 'text-amber-700'}`}>
                  {synthBenefForm.beneficiaire_ids.length === 1 
                    ? '📄 Synthèse individuelle - 1 PDF avec le détail complet du bénéficiaire'
                    : `📑 Synthèse groupée - 1 PDF avec ${synthBenefForm.beneficiaire_ids.length} pages (1 page par bénéficiaire)`
                  }
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSyntheseBenefDialog(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={handleGenerateSyntheseBenef} disabled={generatingSynth || synthBenefForm.beneficiaire_ids.length === 0} className="bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="btn-generate-synthese-pdf">
              {generatingSynth ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Génération...</> : <><Download className="w-4 h-4 mr-2" />Télécharger PDF</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
